import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, Archive, X } from "lucide-react";
import { MergeDialog } from "@/components/clientmaster/MergeDialog";

function Side({ c }) {
  return (
    <div className="text-xs min-w-0">
      <div className="font-medium text-sm truncate">{c.client_name}</div>
      <div className="text-[var(--ink-2)] truncate">{c.business_name || "—"} · {c.registered_no || "no company no."}</div>
      <div className="text-[var(--ink-2)] truncate">{c.email || "—"} · {c.phone || "—"}</div>
      <div className="text-[10px] font-mono-data text-[var(--ink-2)]">{c.client_uid || "No ID"} · {c.status || "—"}</div>
    </div>
  );
}

export function DuplicatesTab({ onChange }) {
  const [pairs, setPairs] = useState(null);
  const [merging, setMerging] = useState(null);
  const load = () => api.get("/client-master/duplicates").then((r) => setPairs(r.data));
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChange(); };

  async function notDup(p) {
    try { await api.post("/client-master/duplicates/not-duplicate", { a_id: p.a.id, b_id: p.b.id }); toast.success("Marked as not a duplicate"); done(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function archive(c) {
    if (!window.confirm(`Archive ${c.client_name}? It will be kept, but hidden and its Client ID released.`)) return;
    try { await api.patch(`/clients/${c.id}/archive`); toast.success("Client archived"); done(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  if (!pairs) return <div className="text-sm text-[var(--ink-2)]">Scanning clients…</div>;
  if (!pairs.length) return <div className="card-flat p-8 text-center text-sm text-[var(--ink-2)]" data-testid="dupes-empty">No possible duplicates found among active clients.</div>;
  return (
    <div className="space-y-3" data-testid="dupes-list">
      <div className="text-xs text-[var(--ink-2)]">{pairs.length} possible duplicate pair{pairs.length === 1 ? "" : "s"}. Matched on client name, company name and company number; email and phone are supporting evidence only.</div>
      {pairs.map((p) => (
        <div key={p.key} className="card-flat p-4" data-testid={`dupe-pair-${p.key}`}>
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className={`status-pill ${p.confidence === "High" ? "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" : "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]"}`}>{p.confidence}</span>
            {p.reasons.map((r) => <span key={r} className="status-pill border-[var(--navy)] text-[var(--navy)]">{r}</span>)}
            {p.evidence.map((r) => <span key={r} className="status-pill border-[var(--line)] text-[var(--ink-2)]">+ {r}</span>)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-center">
            <Side c={p.a} /><Side c={p.b} />
            <div className="flex flex-wrap gap-1.5 justify-end">
              <button data-testid={`dupe-merge-${p.key}`} onClick={() => setMerging(p)} className="btn-primary text-xs !py-1 !px-2 flex items-center gap-1"><GitMerge size={12} /> Merge</button>
              <button data-testid={`dupe-archive-a-${p.key}`} onClick={() => archive(p.a)} className="btn-ghost text-xs !py-1 !px-2 flex items-center gap-1" title={`Archive ${p.a.client_name}`}><Archive size={12} /> Archive left</button>
              <button data-testid={`dupe-archive-b-${p.key}`} onClick={() => archive(p.b)} className="btn-ghost text-xs !py-1 !px-2 flex items-center gap-1" title={`Archive ${p.b.client_name}`}><Archive size={12} /> Archive right</button>
              <button data-testid={`dupe-notdup-${p.key}`} onClick={() => notDup(p)} className="btn-ghost text-xs !py-1 !px-2 flex items-center gap-1"><X size={12} /> Not a duplicate</button>
            </div>
          </div>
        </div>
      ))}
      {merging && <MergeDialog pair={merging} onClose={(ok) => { setMerging(null); if (ok) done(); }} />}
    </div>
  );
}
